import { Html } from '@react-three/drei';
import type { ThreeEvent } from '@react-three/fiber';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as THREE from 'three';

import { alignmentsAtom, alignmentWalkAtom } from '@/atoms/alignment.atom';
import { clearanceEnvelopeVisibleAtom, clearancePreviewAtom } from '@/atoms/clearance.atom';
import { stationVolumeHoverBinAtom, stationVolumeSelectionAtom } from '@/atoms/stationVolumes.atom';
import { HIGHLIGHT } from '@/components/3D/AlignmentOverlay';
import { useClearanceRunSource } from '@/components/3D/useClearanceRunSource';
import { pointAtStation } from '@/core/alignment';
import { clearanceBandColor, clearanceBandState } from '@/lib/clearance/bands';
import { clearanceEnvelopeSource } from '@/lib/clearance/envelope';
import {
  buildBandedEnvelope,
  buildEnvelopeGeometry,
  buildEnvelopeOutline,
  envelopeSampleStep,
  paintBandedEnvelope,
  type EnvelopeBand,
} from '@/lib/clearance/envelopeGeometry';
import { profileError } from '@/lib/clearance/profile';
import { binCentreStation } from '@/lib/stationVolumes/aggregate';
import { formatFixed, formatStationRange } from '@/lib/stationVolumes/format';

/** atomFamily needs a key even when no dock is open. */
const NO_LAYER = '__none';

/** Alpha for the plain, un-banded tube — the dialog preview, which carries one
 *  flat tint and must not obscure the cloud the user is fitting it to. */
const PLAIN_OPACITY = 0.2;
/**
 * Alpha for the BANDED tube. Higher than the plain one on purpose: 0.2 was
 * right for a single tint whose only job was to show where the profile sweeps,
 * but a per-bin verdict is a reading — at 0.2 a red span and an amber one are
 * two barely-distinguishable washes over whatever happens to be behind them,
 * and the reader cannot tell a violation from a near miss without going back
 * to the chart. The tube is still see-through enough to judge the points
 * inside it, which is the other half of its job.
 */
const BANDED_OPACITY = 0.35;

/**
 * The swept clearance envelope: a translucent tube plus its cross-sections and
 * rails, along the analysed alignment.
 *
 * Drawn from a SOURCE UNION (`clearanceEnvelopeSource`, `lib/clearance/envelope.ts`):
 * either the run-dialog's live preview (`clearancePreviewAtom`, while the
 * dialog is open) or a finished run's own immutable snapshot (`useClearance`'s
 * `json`, gated by `clearanceEnvelopeVisibleAtom`). The dialog always wins —
 * see that module's docstring for why — and the run source is drawn on the
 * CURRENT centreline (`useClearanceRunSource`'s `alignment`).
 *
 * TWO MESHES, one component. The dialog preview has no bins, so it gets the
 * plain single-tint tube (`buildEnvelopeGeometry`). A finished run gets the
 * BANDED tube (`buildBandedEnvelope`): one span per station bin, tinted by its
 * K2 verdict, dimmed outside the dock's brush, brightened under the pointer,
 * and pickable — hover sets `stationVolumeHoverBinAtom` and a click walks the
 * cursor to the bin. That is the corridor-plates feature, absorbed. The plates
 * were flat slabs at the alignment datum, i.e. the same plane as this tube's
 * floor for any profile based at height 0, so they read as the envelope's
 * bottom while saying something else entirely.
 *
 * The stale-alignment amber (P5) OUTRANKS the verdict tint and composes with
 * it by REPLACING it: when the centreline has drifted, every band is painted
 * `presentation.silhouette` instead of its verdict. Per-bin verdicts placed on
 * a centreline the run never measured would be precisely the confident lie the
 * amber exists to prevent — but the geometry stays banded, so hover, selection
 * and click-to-walk keep working on a stale run exactly as the plates did.
 *
 * There are no interactive handles here, so the screen-space sizing rule does
 * not apply — every vertex is a real world-space position. What DOES apply is
 * the floating-origin rule: the geometry is built relative to the alignment's
 * first vertex and the group is positioned there, because UTM coordinates
 * jitter visibly at Float32 precision.
 */
export const ClearanceEnvelope = () => {
  const { t } = useTranslation('project');
  const preview = useAtomValue(clearancePreviewAtom);
  const { dock, json, alignment: runAlignment, palette, presentation } = useClearanceRunSource();
  const envelopeOn = useAtomValue(clearanceEnvelopeVisibleAtom(dock?.layerId ?? NO_LAYER));
  const alignments = useAtomValue(alignmentsAtom);
  const selection = useAtomValue(stationVolumeSelectionAtom);
  const hoverBin = useAtomValue(stationVolumeHoverBinAtom);
  const setHover = useSetAtom(stationVolumeHoverBinAtom);
  const setWalk = useSetAtom(alignmentWalkAtom);

  /** True while the pointer is over the tube — see the hover-release effect. */
  const hoveringRef = useRef(false);

  const source = useMemo(
    () => clearanceEnvelopeSource(preview, !!dock, envelopeOn, json ?? null, presentation.silhouette),
    [preview, dock, envelopeOn, json, presentation.silhouette],
  );

  // The dialog preview names its OWN alignment (any alignment in the
  // registry, not necessarily the one an open dock is about); the run source
  // is drawn on the dock's current centreline.
  const alignment = source ? (preview ? alignments.get(source.key) : runAlignment) : undefined;
  // The dialog only publishes a valid profile, but the atom is public and the
  // geometry builders assume a well-formed polygon — a self-crossing ring
  // would render as a knot rather than as nothing. A run's own snapshot was
  // already validated server-side before it could run, but re-checking here
  // costs nothing and keeps this component's guarantee identical either way.
  const valid = source ? profileError(source.vertices) === null : false;

  const origin = useMemo(
    () =>
      alignment
        ? new THREE.Vector3(alignment.points[0], alignment.points[1], alignment.points[2])
        : new THREE.Vector3(),
    [alignment],
  );

  const { fromStation, toStation, step } = useMemo(() => {
    if (!alignment || !source) return { fromStation: 0, toStation: 0, step: 1 };
    const from = source.from ?? 0;
    const to = source.to ?? alignment.length;
    // Hard-capped at MAX_ENVELOPE_RINGS rings, whatever the corridor length
    // (ruling P8) — see `envelopeSampleStep`'s docstring for why there is no
    // upper clamp on the step itself.
    return { fromStation: from, toStation: to, step: envelopeSampleStep(to - from) };
  }, [alignment, source]);

  /**
   * One band per station bin, or null while the source is the dialog preview
   * (which has measured nothing and has nothing to say about any bin).
   *
   * The colour comes from `clearanceBandColor` — `clearanceBandState` over
   * `CLEARANCE_K2_KEY`, the very map the chart's bars and both legends are
   * painted from (ruling P14). There is no second colour table here, so a
   * light/dark flip repaints the chart and the tube together.
   */
  const bands = useMemo((): EnvelopeBand[] | null => {
    if (preview || !source || !json) return null;
    return json.bins.map((b) => ({
      fromStation: b.staStartInternalM,
      toStation: b.staEndInternalM,
      color: presentation.silhouette ?? clearanceBandColor(b, palette),
    }));
  }, [preview, source, json, palette, presentation.silhouette]);

  const banded = useMemo(() => {
    if (!alignment || !source || !valid || !bands || bands.length === 0) return null;
    return buildBandedEnvelope(alignment, source.vertices, source.verticalOffsetM, bands, step, origin);
  }, [alignment, source, valid, bands, step, origin]);

  // The plain tube is the fallback, not the default: a finished run whose bins
  // built a banded tube must not also draw an untinted one over itself.
  const geometry = useMemo(() => {
    if (!alignment || !source || !valid || banded) return null;
    return buildEnvelopeGeometry(
      alignment,
      source.vertices,
      source.verticalOffsetM,
      fromStation,
      toStation,
      step,
      origin,
    );
  }, [alignment, source, valid, banded, fromStation, toStation, step, origin]);

  const outline = useMemo(() => {
    if (!alignment || !source || !valid) return null;
    const { sections, rails } = buildEnvelopeOutline(
      alignment,
      source.vertices,
      source.verticalOffsetM,
      fromStation,
      toStation,
      step,
      origin,
    );
    const tint = source.qualified ?? HIGHLIGHT;
    // Two weights, one hue. A cross-section is a real measured shape and holds
    // full strength; the rails between stations are interpolation, and there
    // is one per profile vertex per ring — at the same opacity they mass into
    // a solid tube that buries both the sections and the points inside it.
    const build = (geom: THREE.BufferGeometry, opacity: number, renderOrder: number) => {
      const obj = new THREE.LineSegments(
        geom,
        new THREE.LineBasicMaterial({ color: tint, depthTest: false, transparent: true, opacity }),
      );
      obj.renderOrder = renderOrder;
      obj.frustumCulled = false;
      return obj;
    };
    // Rails first, so where the two cross the section wins the blend.
    return [build(rails, 0.28, 999), build(sections, 0.9, 1000)];
  }, [alignment, source, valid, fromStation, toStation, step, origin]);

  useEffect(() => () => geometry?.dispose(), [geometry]);
  useEffect(() => () => banded?.geometry.dispose(), [banded]);
  useEffect(() => {
    if (!outline) return;
    return () => {
      for (const obj of outline) {
        obj.geometry.dispose();
        (obj.material as THREE.Material).dispose();
      }
    };
  }, [outline]);

  // Brush emphasis is scoped to the dock's own layer: a stale selection from a
  // previously open dock must not dim a different run's tube.
  const selRange = selection && dock && selection.layerId === dock.layerId ? selection : null;

  // Emphasis is NOT part of the build — the app can flip between light and dark
  // and the reader can drag a brush with the tube on screen, and re-sweeping
  // every bin's rings to repaint them would be absurd. A layout effect, not a
  // plain one, so the attribute is filled inside the same commit the mesh is
  // mounted in — r3f's next rAF cannot land between the two, so there is no
  // frame showing the previous selection.
  useLayoutEffect(() => {
    if (!banded || !bands) return;
    paintBandedEnvelope(banded, bands, { selection: selRange, hoverBin, hoverInk: palette.ink });
  }, [banded, bands, selRange, hoverBin, palette.ink]);

  // Turning the envelope off (or closing the dock) mid-hover never fires
  // `onPointerOut`, so the chart and the table would keep highlighting a bin
  // nobody is pointing at. Only this component's own hover is released — a
  // hover the chart owns must survive.
  useEffect(() => {
    if (banded) return;
    if (hoveringRef.current) {
      hoveringRef.current = false;
      setHover(null);
    }
  }, [banded, setHover]);

  const tip = useMemo(() => {
    if (!json || !alignment || !banded || hoverBin == null) return null;
    const bin = json.bins[hoverBin];
    if (!bin) return null;
    const p = pointAtStation(alignment, binCentreStation(bin));
    return {
      position: [p.x - origin.x, p.y - origin.y, p.z - origin.z] as [number, number, number],
      range: formatStationRange(bin, 2),
      state: clearanceBandState(bin),
      violations: bin.violationCount,
      // An em dash, never a zero: a bin with no violation has no intrusion
      // depth, which is not the same fact as a depth of 0.000 m.
      depth: bin.maxIntrusionM === null ? '—' : formatFixed(bin.maxIntrusionM, 3),
      near: bin.nearMiss.within0_35M,
    };
  }, [json, alignment, banded, hoverBin, origin]);

  /** Which bin the pointer is over. `vertexBin[face.a]` — the exact shape the
   *  corridor plates picked with, so the two can never drift apart. */
  const pick = (e: ThreeEvent<PointerEvent | MouseEvent>): number | null => {
    const face = e.face;
    if (!banded || !face) return null;
    const bin = banded.vertexBin[face.a];
    return bin == null ? null : bin;
  };

  if ((!geometry && !banded) || !outline || !source) return null;

  const tint = source.qualified ?? HIGHLIGHT;

  return (
    <group position={origin}>
      {banded ? (
        <mesh
          geometry={banded.geometry}
          renderOrder={3}
          onPointerMove={(e) => {
            e.stopPropagation();
            hoveringRef.current = true;
            setHover(pick(e));
          }}
          onPointerOut={() => {
            hoveringRef.current = false;
            setHover(null);
          }}
          onClick={(e) => {
            e.stopPropagation();
            // An orbit/pan that happens to start and end on the tube is not a
            // click on it; every other picker in this app ignores a drag.
            if (e.delta > 4) return;
            const i = pick(e);
            const bin = i == null || !json ? null : json.bins[i];
            if (!bin || !json) return;
            const station = binCentreStation(bin);
            // Same contract the corridor plates had: move the cursor, never
            // turn `follow` on behind the user's back.
            setWalk((w) => (w && w.key === json.alignment.key ? { ...w, station } : w));
          }}>
          {/* `vertexColors` with an RGBA colour attribute: the verdict hue, the
              hover brighten and the selection dim are all resolved on the CPU
              in `paintBandedEnvelope` and ride this one buffer. No custom
              shader, and no material per band. */}
          <meshBasicMaterial
            vertexColors
            transparent
            opacity={BANDED_OPACITY}
            side={THREE.DoubleSide}
            depthWrite={false}
            depthTest={false}
          />
        </mesh>
      ) : (
        <mesh geometry={geometry!} renderOrder={3} raycast={() => null}>
          <meshBasicMaterial
            color={tint}
            transparent
            opacity={PLAIN_OPACITY}
            side={THREE.DoubleSide}
            depthWrite={false}
            depthTest={false}
          />
        </mesh>
      )}
      {outline.map((obj, i) => (
        <primitive key={i} object={obj} />
      ))}
      {tip && (
        <Html
          position={tip.position}
          center
          style={{ pointerEvents: 'none', userSelect: 'none' }}
          zIndexRange={[10, 1]}>
          <div
            style={{
              transform: 'translate(-50%, calc(-100% - 12px))',
              padding: '4px 8px',
              borderRadius: 6,
              // The tooltip sits on the scene, not on the dock, so it cannot
              // use `var(--sv-*)` — it takes the same palette the tube does.
              background: `color-mix(in srgb, ${palette.surface} 92%, transparent)`,
              border: `1px solid ${palette.zero}`,
              color: palette.ink,
              fontSize: 11,
              lineHeight: 1.4,
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
            }}>
            <div
              style={{
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                fontVariantNumeric: 'tabular-nums',
              }}>
              STA {tip.range}
            </div>
            <div style={{ fontVariantNumeric: 'tabular-nums', opacity: 0.92 }}>
              <span style={{ color: palette.cut }}>{t('clearance.bin.violations')}</span> {tip.violations}
              <span style={{ opacity: 0.45 }}> · </span>
              {t('clearance.bin.depth')} {tip.depth} m<span style={{ opacity: 0.45 }}> · </span>
              <span style={{ color: palette.warn }}>{t('clearance.bin.near')}</span> {tip.near}
            </div>
            {tip.state === 'empty' && <div style={{ color: palette.warn }}>{t('clearance.bin.noPoints')}</div>}
          </div>
        </Html>
      )}
    </group>
  );
};
